import { HelpBtn } from './HelpBtn';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Crosshair, Route, FileText } from 'lucide-react';
import { kksPlanPdf } from '../lib/kksPlanPdf';
import { kksPoints, kksDue, kksMonths, KKS_MONTHS_KEY } from '../lib/inspect';
import { currentPosition, orderStops, routeLegs } from '../lib/route';

export const openKksPlan = () => window.dispatchEvent(new Event('giss-kksplan-open'));
const CLR = { red: '#dc2626', yellow: '#ca8a04', green: '#16a34a' };
const ORD = { red: 0, yellow: 1, green: 2 };

function Row({ t, f, r, i, onOpen }) {
  const d = (ms) => new Date(ms).toLocaleDateString(t.locale);
  return (
    <li className={`kp-row kp-${r.st}`} data-testid={`kks-plan-item-${i}`}>
      <i style={{ background: CLR[r.st] }} />
      <span><b>{f.name || t.fit_kks}</b>
        <small data-testid={`kks-plan-status-${i}`}>{!r.last ? t.kpNever : `${t.kpLast} ${d(r.last)} · ${t.kpDue} ${d(r.due)} · ${r.left < 0 ? t.scOver.replace('{n}', -r.left) : t.scLeft.replace('{n}', r.left)}`}</small></span>
      <button className="btn btn-outline btn-sm" data-testid={`kks-plan-open-${i}`} onClick={() => onOpen(f)}><Crosshair size={14} /> {t.kaOpen}</button>
    </li>
  );
}

// yearly KKS measuring plan with traffic light + shortest route over all due points
export default function KksPlan({ t, project, mapRef, onOpen }) {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(kksMonths);
  const [legs, setLegs] = useState(null);
  useEffect(() => { const on = () => { setOpen(true); setLegs(null); }; window.addEventListener('giss-kksplan-open', on); return () => window.removeEventListener('giss-kksplan-open', on); }, []);
  if (!open) return null;
  const list = kksPoints(project).map((f) => ({ f, r: kksDue(f, months) })).sort((a, b) => ORD[a.r.st] - ORD[b.r.st] || (a.r.due ?? 0) - (b.r.due ?? 0));
  const due = list.filter((x) => x.r.st !== 'green');
  const setM = (v) => { const n = Math.max(1, Math.min(60, Number(v) || 12)); setMonths(n); localStorage.setItem(KKS_MONTHS_KEY, String(n)); setLegs(null); };
  const plan = async () => {
    const c = mapRef.current.getCenter();
    const start = await currentPosition({ lat: c.lat, lon: c.lng });
    setLegs(routeLegs(start, orderStops(start, due.map(({ f }) => ({ lat: f.lat, lon: f.lon, name: f.name })))));
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="kks-plan-dialog" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="editor kp-ed">
        <div className="row pf-head"><h4>📅 {t.kpTitle}</h4><HelpBtn k="n:gas_extra" testid="kks-plan-help" title={t.helpHere} /><button className="icon-btn" data-testid="kks-plan-close-btn" onClick={() => setOpen(false)}><X size={18} /></button></div>
        <label className="kp-int">{t.kpInterval}<input inputMode="numeric" data-testid="kks-plan-months" value={months} onChange={(e) => setM(e.target.value)} /></label>
        <p className="muted" data-testid="kks-plan-summary">{list.filter((x) => x.r.st === 'red').length} {t.kpRed} · {list.filter((x) => x.r.st === 'yellow').length} {t.kpYellow} · {list.filter((x) => x.r.st === 'green').length} {t.kpGreen}</p>
        {!list.length && <p className="muted" data-testid="kks-plan-empty">{t.kpEmpty}</p>}
        <ul className="kp-list" data-testid="kks-plan-list">{list.map(({ f, r }, i) => <Row key={f.id} t={t} f={f} r={r} i={i} onOpen={(x) => { setOpen(false); onOpen(x); }} />)}</ul>
        {list.length > 0 && <button className="btn btn-outline" data-testid="kks-plan-pdf-btn" onClick={() => kksPlanPdf(t, project, list, months)}><FileText size={16} /> {t.kpPdf}</button>}
        {due.length > 0 && <button className="btn btn-green" data-testid="kks-plan-route-btn" onClick={plan}><Route size={16} /> {t.kpRoute.replace('{n}', due.length)}</button>}
        {legs && (
          <div className="kp-legs" data-testid="kks-plan-legs">
            {legs.map((l, i) => <a key={l.url} className="btn btn-outline btn-sm" data-testid={`kks-plan-leg-${i}`} href={l.url} target="_blank" rel="noreferrer">🗺️ {t.kpLeg} {i + 1} ({l.stops.length})</a>)}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
